// src/auth/RequireAdmin.jsx
//
// Route guard for admin-only pages (e.g. /admin).
// Unlike RequireAuth, this checks the person's Firestore role, not
// just whether they're logged in — a regular logged-in user must be
// blocked here too.
//
// This replaces the old auth/ProtectedRoute.js (same idea, renamed so
// its purpose is obvious next to RequireAuth) AND fixes the bug where
// App.js was actually wiring "/admin" to auth/Dashboard/ProtectedRoute.js,
// a file that only checked "is someone logged in" and never checked
// role at all. That file should be deleted once App.js points here.
//
// Usage in App.js:
//   <Route path="/admin" element={
//     <RequireAdmin><AdminSignupPage /></RequireAdmin>
//   } />

import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { checkAdmin } from "../utils/AuthHelpers";

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
const RequireAdmin = ({ children }) => {
    // null = still checking, true/false = result of the admin check
    const [isAdmin, setIsAdmin] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const verifyAdminRole = async () => {
            const result = await checkAdmin();
            if (isMounted) setIsAdmin(result);
        };

        verifyAdminRole();

        // Avoid setting state after this component has unmounted
        // (e.g. the user navigates away before the check finishes).
        return () => {
            isMounted = false;
        };
    }, []);

    const isStillChecking = isAdmin === null;

    if (isStillChecking) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
};

export default RequireAdmin;
